import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, onSnapshot, collection, addDoc, serverTimestamp, updateDoc, query, orderBy, limit, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import '../styles/ChatScreen.css';

const ChatScreen = () => {
    const { chatId } = useParams();
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [chatInfo, setChatInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [otherUser, setOtherUser] = useState(null);
    const messagesEndRef = useRef(null);
    const navigate = useNavigate();

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        if (!auth.currentUser) {
            navigate('/login');
            return;
        }

        const currentUserId = auth.currentUser.uid;

        const loadChat = async () => {
            try {
                if (chatId === 'new') {
                    // Modo de nuevo chat
                    setChatInfo({ isNewChat: true });
                    setLoading(false);
                    return;
                }

                // Cargar información del chat
                const chatDoc = await getDoc(doc(db, 'chats', chatId));
                if (!chatDoc.exists()) {
                    navigate('/dashboard/chat');
                    return;
                }

                const chatData = chatDoc.data();
                const otherParticipantId = chatData.participants.find(id => id !== currentUserId);

                // Obtener información del otro usuario
                const userDoc = await getDoc(doc(db, 'users', otherParticipantId));
                const userData = userDoc.exists() ? userDoc.data() : {
                    displayName: 'Usuario desconocido',
                    photoURL: null
                };

                setChatInfo({
                    id: chatDoc.id,
                    ...chatData,
                    otherParticipantId
                });

                setOtherUser({
                    id: otherParticipantId,
                    ...userData
                });

                // Marcar mensajes como leídos
                if (chatData.unreadCount && chatData.unreadCount[currentUserId] > 0) {
                    const unreadUpdate = { ...chatData.unreadCount };
                    unreadUpdate[currentUserId] = 0;

                    await updateDoc(doc(db, 'chats', chatId), {
                        unreadCount: unreadUpdate
                    });
                }

                setLoading(false);
            } catch (error) {
                console.error('Error loading chat:', error);
                navigate('/dashboard/chat');
            }
        };

        loadChat();

        // Suscribirse a mensajes en tiempo real
        if (chatId !== 'new') {
            const messagesQuery = query(
                collection(db, 'chats', chatId, 'messages'),
                orderBy('timestamp', 'asc'),
                limit(50)
            );

            const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
                const messagesData = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setMessages(messagesData);
            });

            return () => unsubscribe();
        }
    }, [chatId, navigate]);

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || !auth.currentUser) return;

        try {
            if (chatId === 'new') {
                // Crear nuevo chat
                const otherUserId = prompt('Ingresa el ID del usuario con quien quieres chatear:');
                if (!otherUserId) return;

                // Verificar que el usuario exista
                const userDoc = await getDoc(doc(db, 'users', otherUserId));
                if (!userDoc.exists()) {
                    alert('Usuario no encontrado');
                    return;
                }

                // Crear nuevo chat
                const newChatRef = await addDoc(collection(db, 'chats'), {
                    participants: [auth.currentUser.uid, otherUserId],
                    lastMessage: null,
                    lastMessageTimestamp: null,
                    lastMessageSender: null,
                    unreadCount: {
                        [auth.currentUser.uid]: 0,
                        [otherUserId]: 1
                    },
                    createdAt: serverTimestamp()
                });

                navigate(`/dashboard/chat/${newChatRef.id}`);
            } else {
                // Enviar mensaje en chat existente
                await addDoc(collection(db, 'chats', chatId, 'messages'), {
                    text: newMessage,
                    senderId: auth.currentUser.uid,
                    timestamp: serverTimestamp(),
                    read: false
                });

                // Actualizar información del chat
                await updateDoc(doc(db, 'chats', chatId), {
                    lastMessage: {
                        text: newMessage,
                        senderId: auth.currentUser.uid
                    },
                    lastMessageTimestamp: serverTimestamp(),
                    lastMessageSender: auth.currentUser.uid,
                    unreadCount: {
                        ...chatInfo.unreadCount,
                        [otherUser.id]: (chatInfo.unreadCount?.[otherUser.id] || 0) + 1
                    }
                });

                setNewMessage('');
            }
        } catch (error) {
            console.error('Error sending message:', error);
            alert('Error al enviar el mensaje');
        }
    };

    if (loading) {
        return (
            <div className="chat-screen-container">
                <div className="loading-spinner">Cargando chat...</div>
            </div>
        );
    }

    if (chatInfo?.isNewChat) {
        return (
            <div className="chat-screen-container">
                <div className="new-chat-instructions">
                    <h2>Nuevo Chat</h2>
                    <p>Ingresa el ID del usuario con quien quieres comenzar una conversación.</p>
                    <button
                        className="start-new-chat-btn"
                        onClick={() => {
                            const otherUserId = prompt('ID del usuario:');
                            if (otherUserId) {
                                navigate(`/dashboard/chat/new?user=${otherUserId}`);
                            }
                        }}
                    >
                        Iniciar Nuevo Chat
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="chat-screen-container">
            <div className="chat-header">
                <button className="back-btn" onClick={() => navigate('/dashboard/chat')}>
                    ← Volver
                </button>
                <div className="chat-user-info">
                    <div className="chat-user-avatar">
                        {otherUser?.photoURL ? (
                            <img src={otherUser.photoURL} alt="Avatar" />
                        ) : (
                            <div className="default-avatar">
                                {otherUser?.displayName?.charAt(0) || 'U'}
                            </div>
                        )}
                    </div>
                    <div className="chat-user-details">
                        <h3>{otherUser?.displayName || 'Usuario'}</h3>
                        <span className="chat-status">En línea</span>
                    </div>
                </div>
            </div>

            <div className="messages-container">
                {messages.length === 0 ? (
                    <div className="no-messages">
                        <p>No hay mensajes aún.</p>
                        <p>¡Comienza la conversación!</p>
                    </div>
                ) : (
                    messages.map(message => (
                        <div
                            key={message.id}
                            className={`message ${message.senderId === auth.currentUser.uid ? 'sent' : 'received'}`}
                        >
                            <div className="message-content">
                                {message.text}
                            </div>
                            <div className="message-time">
                                {message.timestamp?.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </div>
                        </div>
                    ))
                )}
                <div ref={messagesEndRef} />
            </div>

            <form className="message-input-container" onSubmit={handleSendMessage}>
                <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Escribe un mensaje..."
                    className="message-input"
                    disabled={!otherUser}
                />
                <button
                    type="submit"
                    className="send-btn"
                    disabled={!newMessage.trim() || !otherUser}
                >
                    Enviar
                </button>
            </form>
        </div>
    );
};

export default ChatScreen;